import { Box, Tab, formatMinutes, getSpacing, idToColor, pn, Skeleton, Stack } from "@compass/core";
import { Tabs } from "@mui/material";
import { Chart, Export, Label, Legend, Series, Tooltip, ValueAxis, VisualRange, Title, Font } from "devextreme-react/chart";
import { HomeServiceDashboardRow } from "generated/openApi";
import { useServiceData } from "../contexts";
import { DashboardCard } from "../components/DashboardCard";
import { useRef } from "react";
import { ServiceFilterControls } from "./ServiceFilterControls";
import { ServicePeriod } from "../types";

const ServiceChart = (props: { 
    rows: HomeServiceDashboardRow[] | undefined; 
    loading: boolean;
    chartRef: React.RefObject<Chart>;
    periodLabel: string;
}) => {
    const exportDate = new Date().toLocaleDateString();
    
    return props.loading ? (
        <Stack direction="row" spacing="lg">
            <Skeleton variant="rounded" width={"70%"} height={400} />
            <Skeleton variant="rounded" width={"30%"} height={200} />
        </Stack>
    ) : (
        <Stack width="100%" height="100%" p={getSpacing("md")}>
            <Chart
                ref={props.chartRef}
                id="chart"
                dataSource={props.rows}
                height="100%"
                customizePoint={(args: any) => {
                    return { color: idToColor(args.data.serviceTypeId) };
                }}
                onPointHoverChanged={(pointInfo: any) => {
                    if (pointInfo.target.isHovered()) {
                        pointInfo.target.showTooltip();
                    } else {
                        pointInfo.target.hideTooltip();
                    }
                }}
            >
                <Title 
                    text="Services Dashboard"
                    subtitle={`Period: ${props.periodLabel} | Exported: ${exportDate}`}
                >
                    <Font size={18} weight={600} />
                </Title>
                <Series
                    argumentField={pn<HomeServiceDashboardRow>("serviceType")}
                    valueField={pn<HomeServiceDashboardRow>("totalStudents")}
                    type="bar"
                />
                <Tooltip
                    enabled={false}
                    customizeTooltip={(arg) => {
                        return {
                            text:
                                `${arg.point.data.serviceType}<br/>` +
                                `Students: ${arg.point.data.totalStudents}<br/>` +
                                `Total: ${formatMinutes(arg.point.data.totalDurationInMinutes)}`,
                        };
                    }}
                />
                <ValueAxis>
                    <VisualRange startValue={0} />
                    <Label customizeText={(arg) => `${arg.valueText}`} />
                </ValueAxis>
                <Legend visible={false} />
                <Export enabled={true} />
            </Chart>
        </Stack>
    );
};

const ServiceChartCardContent = (props: { 
    chartRef: React.RefObject<Chart>;
    periodLabel: string;
}) => {
    const { currentRows, loading, selectedPeriod, updateSelectedPeriod } = useServiceData();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: ServicePeriod) => {
        updateSelectedPeriod(newValue);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center" 
                sx={{ mb: 2 }}
            >
                <Tabs value={selectedPeriod} onChange={handleTabChange}>
                    <Tab label="This Month" value={ServicePeriod.ThisMonth} />
                    <Tab label="Previous Month" value={ServicePeriod.PreviousMonth} />
                </Tabs>
                <ServiceFilterControls />
            </Stack>

            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <ServiceChart 
                    rows={currentRows} 
                    loading={loading} 
                    chartRef={props.chartRef}
                    periodLabel={props.periodLabel}
                />
            </Box>
        </Box>
    );
};

export const ServiceChartCard = () => {
    const { selectedPeriod } = useServiceData();
    
    // Reference to the Chart instance
    const chartRef = useRef<Chart>(null);

    const periodLabel = selectedPeriod === ServicePeriod.ThisMonth ? "This Month" : "Previous Month";

    // Export handler - passed directly as prop to DashboardCard
    const handleExport = () => {
        if (chartRef.current) {
            const filenamePeriod = selectedPeriod === ServicePeriod.ThisMonth ? "ThisMonth" : "PreviousMonth";
            chartRef.current.instance.exportTo(`Services_${filenamePeriod}`, 'png');
        }
    };

    return (
        <DashboardCard title="Services" onExport={handleExport}>
            <ServiceChartCardContent chartRef={chartRef} periodLabel={periodLabel} />
        </DashboardCard>
    );
};